import React from 'react';

import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import NotAvailable from '@/src/components/notAvailable';
import { BASE_HASH_PATH, generateRoutePath } from '@/src/routes';

import FileEntryViewer from './FileEntryViewer/FileEntryViewer';
import Header from './Header/Header';

import { FileEntryViewerInfoPanelProvider } from '../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.infoPanel.store';
import { FileEntryViewerSidebarActionsProvider } from '../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.sidebarActions.store';
import { FileEntryViewerProvider } from '../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.store';
import { FileEntryViewerTreatmentProvider } from '../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.treatment.store';
import useFileEntryViewerStore from '../../../components/EntryPanel/FileEntryViewer/useFileEntryViewerStore';
import { useMappedFileEntry } from '../../../components/EntryPanel/FileEntryViewer/useMappedFileEntry';
import useSidebarActions from '../../../components/EntryPanel/FileEntryViewer/useSidebarActions';
import { INBOX_ENTRY_VIEWER_PAGES_PATH } from '../../../Config/inbox.routes';
import { InboxEntrySource, InboxEntryType } from '../../../types';
import { useProjectDetails } from '../../useProject';

export const getWebappFileViewerPageUrl = ({
  projectId,
  inboxEntryId,
  withBasePath = true,
}: {
  projectId: string;
  inboxEntryId: number;
  withBasePath?: boolean;
}) => {
  const baseUrl = withBasePath ? window.location.origin + BASE_HASH_PATH : '';

  return (
    baseUrl +
    generateRoutePath(
      INBOX_ENTRY_VIEWER_PAGES_PATH,
      {
        projectId,
        inboxEntryId,
      },
      {
        type: InboxEntryType.DOCUMENT.toLowerCase(),
        source: InboxEntrySource.WebApp,
      }
    )
  );
};

type FileEntryViewerPageProps = {
  fileId: number;
  inboxEntryId: number;
  projectId: string;
};

const FileEntryViewerPage = ({
  fileId,
  inboxEntryId,
  projectId,
}: FileEntryViewerPageProps) => {
  const { isLoading, mappedFileEntry, fileDetailsMutation } = useMappedFileEntry({
    fileId,
  });
  const { project } = useProjectDetails();

  const { sidebarActions, infoPanelQueries, treatmentEvents } = useFileEntryViewerStore({
    fileId,
    inboxEntryId,
    projectId,
  });

  if (!fileDetailsMutation && !isLoading) {
    return <NotAvailable />;
  }

  if (!mappedFileEntry || !project) {
    return <Spinner />;
  }

  return (
    <FileEntryViewerTreatmentProvider treatmentEvents={treatmentEvents}>
      <FileEntryViewerInfoPanelProvider infoPanelQueries={infoPanelQueries}>
        <FileEntryViewerSidebarActionsProvider sidebarActions={sidebarActions}>
          <FileEntryViewerProvider>
            {isLoading && <Spinner />}
            <FileEntryViewer
              header={
                <Header
                  fileEntry={mappedFileEntry}
                  projectName={project.project}
                />
              }
              fileEntry={mappedFileEntry}
            />
          </FileEntryViewerProvider>
        </FileEntryViewerSidebarActionsProvider>
      </FileEntryViewerInfoPanelProvider>
    </FileEntryViewerTreatmentProvider>
  );
};

export default FileEntryViewerPage;
